import styled from 'styled-components';

// ── Info banner
export const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--vo-celeste);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const InfoBannerTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

export const InfoBannerDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.55;
`;

// ── Filter card
export const FilterCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;

export const FilterCardLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  margin: 0 0 16px;
  letter-spacing: .08em;
  text-transform: uppercase;
`;

export const FilterRow1 = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const FilterRow2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 18px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const FilterFieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;

export const FilterActions = styled.div`
  display: flex;
  gap: 10px;
`;

export const FilterClearBtn = styled.button`
  height: 40px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--vo-mist); }
`;

export const FilterSearchBtn = styled.button`
  height: 40px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: var(--vo-cobalto); }
`;

// ── Table
export const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

export const ProcessTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const ProcessTh = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  background: var(--vo-mist);
  white-space: nowrap;
`;

export const ProcessTr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

export const ProcessTd = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

export const TdName = styled(ProcessTd)`
  font-size: 14px;
  color: var(--vo-ink);
  font-weight: 500;
`;

export const TdDesc = styled(ProcessTd)`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--vo-slate);
`;

export const TdCenter = styled(ProcessTd)`
  text-align: center;
`;

// Criticality badge
export const CritBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
  white-space: nowrap;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`;

// Boolean badge (DPIA / LIA)
export const BoolBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  display: inline-block;
  color: ${({ $yes }) => $yes ? 'var(--vo-danger)' : 'var(--vo-slate)'};
  background: ${({ $yes }) => $yes ? 'var(--vo-danger-10)' : 'var(--vo-slate-10)'};
`;

// Table action buttons
export const ActionBtns = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

export const ActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  flex-shrink: 0;
  &:hover {
    background: ${({ $danger }) => $danger ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${({ $danger }) => $danger ? 'var(--vo-danger)' : 'var(--vo-celeste)'};
  }
`;

// Table footer / pagination
export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--vo-mist);
`;

export const TableFooterInfo = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

export const PaginationBtns = styled.div`
  display: flex;
  gap: 4px;
`;

export const PageBtn = styled.button`
  width: 32px;
  height: 32px;
  border: ${({ $active }) => $active ? 'none' : '1px solid var(--vo-line)'};
  background: ${({ $active }) => $active ? 'var(--vo-celeste)' : 'white'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => $active ? '700' : '400'};
  color: ${({ $active }) => $active ? 'white' : 'var(--vo-ink-2)'};
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${({ $active }) => $active ? 'var(--vo-cobalto)' : 'var(--vo-mist)'}; }
`;

// ── Show / Detail page

export const ShowTabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`;

export const ShowTabBtn = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  background: ${({ $active }) => $active ? 'var(--vo-celeste)' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'var(--vo-slate)'};
  font-family: 'Maven Pro', sans-serif !important;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms ease;
  display: flex !important;
  align-items: center;
  justify-content: center;
`;

export const ShowGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

export const ShowMainCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

export const ShowCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

export const ShowFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
`;

export const ShowFieldFull = styled.div`
  grid-column: 1 / -1;
`;

export const ShowFieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 6px;
`;

export const ShowFieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink-2);
  margin: 0;
  line-height: 1.65;
`;

export const ShowSidebarStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ShowSidebarCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

export const ShowSidebarTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--vo-cloud);
`;

export const ShowSidebarFieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;

export const ShowSidebarFieldValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  margin: 0;
`;

export const RelatedDataItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: var(--vo-mist);
  border-radius: 10px;
  & + & { margin-top: 8px; }
`;

export const RelatedDataLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
  font-weight: 500;
`;

export const RelatedDataCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  color: var(--vo-celeste);
  font-weight: 700;
`;

export const VulnerableAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--vo-heatmap-risky);
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--vo-warning) 20%, transparent);
`;

export const ShowChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ShowChipTag = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-celeste);
  background: var(--vo-capri-50);
  padding: 3px 12px;
  border-radius: 20px;
  display: inline-block;
`;

export const UserDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const UserAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Rubik', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

// Data mapping tab
export const DataMapCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

export const DataMapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--vo-cloud);
`;

export const DataMapHeaderText = styled.div``;

export const DataMapHeaderTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

export const DataMapHeaderSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
`;

export const DataMapAddBtn = styled.button`
  height: 40px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 7px;
  &:hover { background: var(--vo-cobalto); }
`;

// Bonds tab
export const BondsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

export const BondCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

export const BondCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--vo-cloud);
`;

export const BondCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

export const ShowHeaderBtn = styled.button`
  height: 40px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex !important;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &:hover { background: var(--vo-cobalto); }
`;

export const ShowHeaderBtnSecondary = styled.button`
  height: 40px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 18px;
  display: flex !important;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  &:hover { background: var(--vo-mist); }
`;

export const BondAddBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: var(--vo-capri-50);
  border-radius: 8px;
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: var(--vo-celeste);
  flex-shrink: 0;
  &:hover { background: var(--vo-capri-100); }
`;

export const BondChipWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const BondChip = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-cobalto);
  background: var(--vo-cobalto-8);
  padding: 6px 14px;
  border-radius: 20px;
  display: inline-block;
`;

export const ImportCsvBtn = styled.button`
  height: 40px;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  display: flex !important;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  flex-shrink: 0;
  &:hover { background: var(--vo-mist); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ── Form layout
export const FormWrap = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 60px;
`;

export const FormCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

export const FormCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

export const FormCardIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $gradient }) => $gradient || 'linear-gradient(135deg,var(--vo-cobalto),var(--vo-capri))'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const FormCardHeaderText = styled.div``;

export const FormCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0;
`;

export const FormCardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

export const FormFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

export const FormFieldFull = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormFieldHalf = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--vo-ink-2);
  ${({ $required }) => $required && `
    &::after { content: ' *'; color: var(--vo-danger); }
  `}
`;

export const FormSelect = styled.select`
  height: 48px;
  border: 1.5px solid var(--vo-steel);
  border-radius: 10px;
  padding: 0 16px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${({ $placeholder }) => $placeholder ? 'var(--vo-steel)' : 'var(--vo-ink)'};
  background: white;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  appearance: auto;
  option { color: var(--vo-ink); }
  option[value=''] { color: var(--vo-steel); }
  &:focus { border-color: var(--vo-celeste); }
  &[data-error='true'] { border-color: var(--vo-danger); }
`;

export const FormFieldError = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  color: var(--vo-danger);
  margin-top: 2px;
`;

// Tag chip input
export const FormTagBox = styled.div`
  min-height: 40px;
  border: 1.5px solid var(--vo-steel);
  border-radius: 10px;
  padding: 8px 12px;
  background: white;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  cursor: text;
  &:focus-within { border-color: var(--vo-celeste); }
`;

export const FormTagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--vo-capri-50);
  color: var(--vo-celeste);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
`;

export const FormTagChipX = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: var(--vo-celeste);
  line-height: 1;
`;

export const FormTagInlineInput = styled.input`
  border: none;
  outline: none;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink);
  background: transparent;
  flex: 1;
  min-width: 100px;
  &::placeholder { color: var(--vo-steel); }
`;

// Impact assessment (DPIA/LIA)
export const FormImpactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;
`;

export const FormImpactField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormImpactTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0;
`;

export const FormImpactDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.5;
`;

export const FormRadioGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 4px;
`;

export const FormRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-ink-2);

  input[type='radio'] {
    accent-color: var(--vo-celeste);
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

export const FormVulnerableBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  padding: 16px;
  background: var(--vo-heatmap-risky);
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--vo-warning) 20%, transparent);

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: var(--vo-celeste);
    cursor: pointer;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const FormVulnerableTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--vo-ink-2);
  margin: 0 0 3px;
`;

export const FormVulnerableDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.5;
`;

// Form action bar
export const FormActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

export const FormBtnPrimary = styled.button`
  height: 44px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 28px;
  white-space: nowrap;
  &:hover { background: var(--vo-cobalto); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const FormBtnCancel = styled.button`
  height: 44px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: white;
  color: var(--vo-slate);
  border: 1.5px solid var(--vo-line);
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 28px;
  white-space: nowrap;
  &:hover { background: var(--vo-mist); }
`;

// ── Form pill tabs
export const FormPillTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`;

export const FormPillTab = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  background: ${({ $active }) => $active ? 'var(--vo-celeste)' : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : 'var(--vo-slate)'};
  font-family: 'Maven Pro', sans-serif !important;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 150ms ease;
  white-space: nowrap;
  display: flex !important;
  align-items: center;
  justify-content: center;
`;
